<template>
  <div class="guide-layout">
    <!-- 顶部导航 -->
    <div class="guide-nav">
      <div class="guide-nav-inner">
        <el-button type="text" size="small" icon="el-icon-back" @click="back" class="guide-back-btn">
          返回列表
        </el-button>
        <div class="guide-nav-title">
          <i class="el-icon-document"></i>
          <span>{{ title }}</span>
        </div>
      </div>
    </div>

    <!-- 主体：左侧导航 + 右侧内容 -->
    <div class="guide-body">
      <!-- 左侧目录 -->
      <aside class="guide-aside">
        <div class="guide-aside-inner">
          <div class="guide-aside-label">目录</div>
          <nav class="guide-toc">
            <a
              v-for="item in navItems"
              :key="item.id"
              href="javascript:;"
              class="guide-toc-item"
              :class="{ 'is-active': activeId === item.id }"
              @click="scrollTo(item.id)"
            >
              <span class="guide-toc-num">{{ item.num }}</span>
              <span class="guide-toc-text">{{ item.text }}</span>
            </a>
          </nav>
        </div>
      </aside>

      <!-- 右侧内容 -->
      <main class="guide-main">
        <div class="guide-container">
          <slot></slot>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: "GuideLayout",
  props: {
    title: {
      type: String,
      default: "使用说明",
    },
    backRoute: {
      type: String,
      default: "/oegen",
    },
    navItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeId: "",
      scrollContainer: null,
    };
  },
  mounted() {
    if (this.navItems.length > 0) {
      this.activeId = this.navItems[0].id;
    }
    // 说明页通常嵌在 el-main 内，滚动容器是 el-main 而非 window
    this.scrollContainer = this.findScrollParent(this.$el);
    var target = this.scrollContainer || window;
    target.addEventListener("scroll", this.handleScroll);
    // 首次同步一次高亮
    this.$nextTick(this.handleScroll);
  },
  beforeDestroy() {
    var target = this.scrollContainer || window;
    target.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    // 向上查找第一个可纵向滚动的祖先元素
    findScrollParent(el) {
      var node = el && el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        var style = window.getComputedStyle(node);
        var overflowY = style.overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
          if (node.scrollHeight > node.clientHeight) {
            return node;
          }
        }
        node = node.parentElement;
      }
      return null;
    },
    back() {
      this.$router.push(this.backRoute);
    },
    scrollTo(id) {
      var el = document.getElementById(id);
      if (!el) return;
      var container = this.scrollContainer || window;
      // 顶部导航条高度约 56px，额外留 16px 间距
      var offset = 72;
      if (container === window) {
        var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: top, behavior: "smooth" });
      } else {
        var containerTop = container.getBoundingClientRect().top;
        var elTop = el.getBoundingClientRect().top;
        var delta = elTop - containerTop - offset;
        container.scrollTo({
          top: container.scrollTop + delta,
          behavior: "smooth",
        });
      }
      this.activeId = id;
    },
    handleScroll() {
      var container = this.scrollContainer || window;
      // 用 getBoundingClientRect 计算元素相对视口的位置更稳
      var containerTop = container === window ? 0 : container.getBoundingClientRect().top;
      var trigger = 90; // 顶部导航 + 一点缓冲
      var current = "";
      for (var i = 0; i < this.navItems.length; i++) {
        var el = document.getElementById(this.navItems[i].id);
        if (!el) continue;
        var relTop = el.getBoundingClientRect().top - containerTop;
        if (relTop <= trigger) {
          current = this.navItems[i].id;
        } else {
          break;
        }
      }
      // 顶部时默认高亮第一项
      if (!current && this.navItems.length > 0) {
        current = this.navItems[0].id;
      }
      if (current) {
        this.activeId = current;
      }
    },
  },
};
</script>

<style scoped>
.guide-layout {
  min-height: 100%;
  background: #f5f7fa;
  color: #303133;
  /* 抵消 el-main 的 padding，让 sticky 导航贴到滚动容器顶部，消除上缘缝隙 */
  margin: -20px -20px 0 -50px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC",
    "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial,
    sans-serif;
}

/* ===== 顶部导航 ===== */
.guide-nav {
  position: sticky;
  top: -20px;
  z-index: 100;
  background: #ffffff;
  border-bottom: 1px solid #ebeef5;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.guide-nav-inner {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  gap: 16px;
  max-width: 1280px;
  margin: 0 auto;
}

.guide-back-btn {
  color: #409eff !important;
  font-weight: 500;
  transition: all 0.2s ease;
}

.guide-back-btn:hover {
  color: #66b1ff !important;
  transform: translateX(-2px);
}

.guide-nav-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.guide-nav-title i {
  color: #409eff;
  font-size: 18px;
}

/* ===== 主体双栏 ===== */
.guide-body {
  display: flex;
  max-width: 1280px;
  margin: 0 auto;
  padding: 20px 24px 48px;
  gap: 24px;
}

/* ===== 左侧目录 ===== */
.guide-aside {
  width: 220px;
  flex-shrink: 0;
}

.guide-aside-inner {
  position: sticky;
  top: 76px;
}

.guide-aside-label {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  letter-spacing: 1px;
  margin-bottom: 12px;
  padding-left: 14px;
}

.guide-toc {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-left: 1px solid #ebeef5;
}

.guide-toc-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  font-size: 13px;
  color: #606266;
  border-left: 2px solid transparent;
  margin-left: -1px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  line-height: 1.5;
  border-radius: 0 4px 4px 0;
}

.guide-toc-item:hover {
  color: #409eff;
  background: rgba(64, 158, 255, 0.06);
}

.guide-toc-item.is-active {
  color: #409eff;
  font-weight: 600;
  border-left-color: #409eff;
  background: rgba(64, 158, 255, 0.1);
}

.guide-toc-num {
  font-family: "JetBrains Mono", "Consolas", Monaco, monospace;
  font-size: 12px;
  font-weight: 600;
  color: #c0c4cc;
  flex-shrink: 0;
  min-width: 18px;
}

.guide-toc-item.is-active .guide-toc-num {
  color: #409eff;
}

.guide-toc-text {
  flex: 1;
}

/* ===== 右侧内容 ===== */
.guide-main {
  flex: 1;
  min-width: 0;
}

.guide-container {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #ebeef5;
  padding: 36px 40px;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .guide-aside {
    width: 180px;
  }

  .guide-container {
    padding: 28px 28px;
  }
}

@media (max-width: 768px) {
  .guide-body {
    padding: 16px 12px 32px;
    gap: 12px;
  }

  .guide-aside {
    display: none;
  }

  .guide-container {
    padding: 24px 18px;
  }

  .guide-nav-inner {
    padding: 10px 14px;
  }
}
</style>
